import { useMemo, useState } from "react";
import { Wallet, Check, RotateCcw, ArrowUpRight } from "lucide-react";
import FittinMark from "@/components/fittin/FittinMark";

/* ------------------------------------------------------------------ */
/* Decorative QR — deterministic from the key id. It is a visual token  */
/* placeholder (the real pass carries a signed token), not a real code. */
/* ------------------------------------------------------------------ */
function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function prng(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function PassQR({ seed = "fittin", size = 56, testid }) {
  const d = useMemo(() => {
    const N = 25;
    const rand = prng(hashString(seed));
    const inFinder = (x, y) =>
      (x < 8 && y < 8) || (x >= N - 8 && y < 8) || (x < 8 && y >= N - 8);
    let p = "";
    const cell = (x, y) => {
      p += `M${x} ${y}h1v1h-1z`;
    };
    // finder patterns
    [
      [0, 0],
      [N - 7, 0],
      [0, N - 7],
    ].forEach(([ox, oy]) => {
      for (let y = 0; y < 7; y++)
        for (let x = 0; x < 7; x++) {
          const ring = x === 0 || y === 0 || x === 6 || y === 6;
          const core = x >= 2 && x <= 4 && y >= 2 && y <= 4;
          if (ring || core) cell(ox + x, oy + y);
        }
    });
    for (let y = 0; y < N; y++)
      for (let x = 0; x < N; x++) {
        if (inFinder(x, y)) continue;
        if (rand() > 0.52) cell(x, y);
      }
    return p;
  }, [seed]);

  return (
    <svg
      data-testid={testid}
      width={size}
      height={size}
      viewBox="0 0 25 25"
      shapeRendering="crispEdges"
      aria-hidden
    >
      <path d={d} fill="#0A0A0A" />
    </svg>
  );
}

const shortKey = (id) => {
  if (!id) return "fk_····";
  const clean = String(id).replace(/^fk_preview_/, "fk_");
  return clean.length > 14 ? `${clean.slice(0, 5)}…${clean.slice(-4)}` : clean;
};

/* Shared dark-pass surface with layered depth. */
function PassSurface({ children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative w-full aspect-[63/88] rounded-[28px] bg-[#0A0A0A] text-white overflow-hidden flex flex-col p-5 sm:p-6 shadow-[0_50px_90px_-40px_rgba(0,0,0,0.65),0_18px_30px_-18px_rgba(0,0,0,0.45)] ring-1 ring-inset ring-white/10 ${className}`}
    >
      {/* sheen */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-1/3 -left-1/4 h-[90%] w-[120%] rotate-[-12deg]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,255,255,0.13), rgba(255,255,255,0.03) 60%, transparent 75%)",
        }}
      />
      {/* fine grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          maskImage: "linear-gradient(to bottom, black 0%, transparent 70%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 70%)",
        }}
      />
      <div className="relative flex flex-col h-full">{children}</div>
    </div>
  );
}

function PassHeader({ right = "FitKey" }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className="rounded-[11px] ring-1 ring-white/15 shadow-[0_6px_14px_-6px_rgba(0,0,0,0.8)]">
          <FittinMark size={34} bg="#141414" fg="#FFFFFF" />
        </span>
        <span className="text-[15px] font-medium tracking-tight">Fittin</span>
      </div>
      <span className="text-[10px] uppercase tracking-[0.26em] text-white/55">{right}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* FitKey pass — front                                                 */
/* ------------------------------------------------------------------ */
export default function FitKeyPass({
  size = { unit: "EU", value: "—" },
  brand = "",
  model = "",
  fitkeyId = "",
  sample = false,
  testid = "fitkey-pass",
  className = "",
}) {
  const pair = [brand, model].filter(Boolean).join(" ");
  return (
    <PassSurface testid={testid} className={className}>
      <PassHeader />

      <div className="mt-5 text-[10px] uppercase tracking-[0.24em] text-white/40">
        Valid in every brand
      </div>

      <div className="mt-auto">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/50">
          Your size
          {sample && (
            <span
              data-testid={`${testid}-sample-tag`}
              className="rounded-full border border-white/15 px-1.5 py-[1px] text-[9px] tracking-[0.2em] text-white/60"
            >
              Sample
            </span>
          )}
        </div>
        <div
          data-testid={`${testid}-size`}
          className="mt-1 flex items-baseline gap-2 leading-none tracking-[-0.045em] font-medium"
        >
          <span className="text-[22px] sm:text-[26px] text-white/55">{size.unit}</span>
          <span className={`text-[64px] sm:text-[76px] ${size.value === "—" ? "text-white/20" : ""}`}>
            {size.value === "—" ? "··" : size.value}
          </span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-5 gap-3 border-t border-white/10 pt-3">
        <div className="col-span-3 min-w-0">
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/45">Reference pair</div>
          <div
            data-testid={`${testid}-brand-line`}
            className="mt-1 text-[13px] text-white/90 truncate"
          >
            {pair || "Your best-fitting shoe"}
          </div>
        </div>
        <div className="col-span-2 min-w-0 text-right">
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/45">Key</div>
          <div className="mt-1 font-mono text-[12px] text-white/80 truncate">{shortKey(fitkeyId)}</div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white text-[#0A0A0A] p-2.5 flex items-center gap-3">
        <div className="rounded-lg bg-white p-1">
          <PassQR seed={fitkeyId || "fittin-sample"} size={52} testid={`${testid}-qr`} />
        </div>
        <div className="min-w-0">
          <div className="text-[11.5px] font-medium leading-tight">Tap to confirm fit or return</div>
          <div className="mt-1 text-[10px] text-neutral-500 leading-tight">
            Signed token · no measurements on the pass
          </div>
        </div>
      </div>
    </PassSurface>
  );
}

/* ------------------------------------------------------------------ */
/* FitKey pass — back / order view (MOCKUP of the wallet experience)   */
/* ------------------------------------------------------------------ */
const VERDICTS = [
  { key: "too_small", label: "Too small" },
  { key: "perfect", label: "Perfect" },
  { key: "too_large", label: "Too large" },
];

export function FitKeyPassBack({
  brand = "",
  model = "",
  size = { unit: "EU", value: "—" },
  testid = "fitkey-pass-back",
  className = "",
}) {
  const [verdict, setVerdict] = useState(null);
  const [returned, setReturned] = useState(false);
  const pair = [brand, model].filter(Boolean).join(" ") || "Your latest pair";

  const reset = () => {
    setVerdict(null);
    setReturned(false);
  };

  return (
    <PassSurface testid={testid} className={className}>
      <PassHeader right="Order" />

      <div className="mt-6 rounded-2xl bg-white/[0.06] ring-1 ring-inset ring-white/10 p-4">
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/45">Latest purchase</div>
        <div className="mt-1.5 flex items-end justify-between gap-3">
          <div className="text-[15px] leading-snug truncate">{pair}</div>
          <div className="font-mono text-[13px] text-white/80 shrink-0">
            {size.unit} {size.value}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/45">Confirm fit</div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {VERDICTS.map((v) => (
            <button
              key={v.key}
              type="button"
              disabled={returned}
              aria-pressed={verdict === v.key}
              data-testid={`${testid}-verdict-${v.key}`}
              onClick={() => setVerdict(v.key)}
              className={`rounded-xl px-2 py-2.5 text-[12px] transition-all disabled:opacity-40 ${
                verdict === v.key
                  ? "bg-white text-[#0A0A0A]"
                  : "bg-white/[0.06] text-white/85 ring-1 ring-inset ring-white/10 hover:bg-white/10"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-auto pt-5">
        {returned ? (
          <div
            data-testid={`${testid}-return-done`}
            className="flex items-center justify-between rounded-xl bg-white/[0.06] ring-1 ring-inset ring-white/10 px-3.5 py-3 text-[12.5px]"
          >
            <span className="inline-flex items-center gap-2">
              <Check className="h-3.5 w-3.5" /> Return requested
            </span>
            <button
              type="button"
              data-testid={`${testid}-reset`}
              onClick={reset}
              className="inline-flex items-center gap-1 text-[11px] text-white/55 hover:text-white"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`${testid}-request-return`}
            onClick={() => setReturned(true)}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-white text-[#0A0A0A] px-4 py-3 text-[13px] hover:bg-neutral-200 transition-all active:scale-[0.99]"
          >
            Request return
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        )}
        <div className="mt-3 flex items-center justify-between text-[10px] text-white/45">
          <span>
            {verdict && !returned
              ? `Fit recorded: ${VERDICTS.find((v) => v.key === verdict)?.label}`
              : "One tap from your Wallet"}
          </span>
          <span className="font-mono">getfittin.com/k/…</span>
        </div>
      </div>
    </PassSurface>
  );
}

/* ------------------------------------------------------------------ */
/* Add-to-Wallet badges                                                */
/* mode="decorative": both call onClick (e.g. scroll to check-in)      */
/* mode="live": link to URLs; missing URL → disabled "Coming soon".    */
/* ------------------------------------------------------------------ */
const WALLETS = [
  { key: "apple", top: "Add to", name: "Apple Wallet" },
  { key: "google", top: "Add to", name: "Google Wallet" },
];

export function WalletBadges({
  mode = "decorative",
  appleUrl = null,
  googleUrl = null,
  onClick,
  testid = "wallet-badges",
  className = "",
}) {
  const urls = { apple: appleUrl, google: googleUrl };
  const anyMissing = mode === "live" && (!appleUrl || !googleUrl);

  return (
    <div data-testid={testid} className={className}>
      <div className="flex flex-wrap items-center gap-2.5">
        {WALLETS.map((w) => {
          const url = urls[w.key];
          const disabled = mode === "live" && !url;
          const inner = (
            <>
              <Wallet className="h-[18px] w-[18px] shrink-0" strokeWidth={1.6} />
              <span className="flex flex-col items-start leading-none text-left">
                <span className="text-[9.5px] text-white/70">{disabled ? "Coming soon" : w.top}</span>
                <span className="mt-0.5 text-[13.5px] font-medium tracking-tight">{w.name}</span>
              </span>
            </>
          );
          const cls = `inline-flex items-center gap-2.5 rounded-xl bg-[#0A0A0A] text-white h-11 pl-3 pr-4 ring-1 ring-inset ring-white/10 transition-all ${
            disabled
              ? "opacity-40 cursor-not-allowed"
              : "hover:bg-neutral-800 active:scale-[0.98]"
          }`;
          const tid = `${testid}-${w.key}`;
          if (mode === "live" && url) {
            return (
              <a key={w.key} href={url} target="_blank" rel="noreferrer" data-testid={tid} className={cls}>
                {inner}
              </a>
            );
          }
          return (
            <button
              key={w.key}
              type="button"
              disabled={disabled}
              aria-disabled={disabled}
              data-testid={tid}
              onClick={disabled ? undefined : onClick}
              className={cls}
            >
              {inner}
            </button>
          );
        })}
      </div>
      {anyMissing && (
        <div data-testid={`${testid}-coming-soon`} className="mt-2 text-[11.5px] text-neutral-500">
          Wallet passes are rolling out. Your FitKey is ready and will be addable here soon.
        </div>
      )}
    </div>
  );
}
